import type { Nullable, NullableFields } from "@msuite/utils/types";
import type { ColumnPinningState, RowData } from "@tanstack/react-table";
import type { Cell, ColumnMeta, CreateColumnMenuOptionsBuilder, Header } from "./types";

type TableStateKey =
	| "visibility"
	| "sorting"
	| "pagination"
	| "pinning"
	| "sizing"
	| "columnFilters"
	| "expanded"
	| "order"
	| "showGroupingHeader";

/**
 * This function is used to get the persited state of the table from the local storage.
 * @param persistenceKey
 * @param key
 * @param defaultValue
 * @param global
 * @returns
 */
export function getPersistenceState<T>(
	persistenceKey: string,
	key: TableStateKey,
	defaultValue: T,
	global = false,
): T {
	let state: string | null = null;
	if (!global) state = localStorage.getItem(`${persistenceKey}-${key}`);
	else state = localStorage.getItem(key);
	return state ? JSON.parse(state) : defaultValue;
}

/** The pinning regions `@tanstack/react-table` 8 persists under the same key as version 9. */
interface LegacyColumnPinningState {
	left?: string[];
	right?: string[];
}

/**
 * Read the persisted column pinning, translating the `left`/`right` regions that
 * `@tanstack/react-table` 8 writes under the same key into the `start`/`end` regions version 9
 * reads. A browser that ran the table on version 8 still holds that shape, and handing it to
 * the table unchanged unpins every column — including the row-selection and actions columns
 * the table pins itself. The persister rewrites the key in the current shape on first render.
 * @param persistenceKey
 * @param defaultValue
 * @returns
 */
export function getPersistedColumnPinning(
	persistenceKey: string,
	defaultValue: ColumnPinningState,
): ColumnPinningState {
	const stored = getPersistenceState<
		(Partial<ColumnPinningState> & LegacyColumnPinningState) | null
	>(persistenceKey, "pinning", null);
	if (!stored) return defaultValue;
	return {
		start: stored.start ?? stored.left ?? [],
		end: stored.end ?? stored.right ?? [],
	};
}

/**
 * This function is used to set the persited state of the table in the local storage.
 * @param key
 * @param value
 */
export function setPersistenceState<T extends object | boolean>(
	key: `${string}-${TableStateKey}`,
	value: T,
): void {
	localStorage.setItem(key, JSON.stringify(value));
}

/**
 * This function is used to provide typesafe metadata for the columns.
 * @param meta
 * @returns
 */
export function createMeta(meta: Partial<ColumnMeta>): ColumnMeta {
	return meta;
}

/**
 * This function is used to get the metadata of the cell.
 * @param cell
 * @returns
 */
export function getCellMeta<T extends RowData>(cell: NullableFields<Cell<T, unknown>>): ColumnMeta {
	if ((cell as any)?.cell) {
		return (cell as any).cell.columnDef.meta as ColumnMeta;
	}
	if (!cell?.column?.columnDef) {
		return {};
	}
	return cell.column.columnDef.meta ?? {};
}

/**
 * This function is used to get the metadata of the header.
 * @param header
 * @returns
 */
export function getHeaderMeta<T extends RowData>(header: Header<T, unknown>): ColumnMeta {
	return header.column.columnDef.meta ?? {};
}

/**
 * This function is used to merge subrows into a flat structure.
 * It will recursively traverse the rows and merge subrows into the main row.
 * @template T
 * @param rows
 * @param subRowKey
 * @returns
 */
export function mergeSubrows<T>(rows: Nullable<T[]>, itemKey: keyof T, subRowKey: keyof T) {
	if (!rows) return [];
	const roots = rows
		.filter((row) => !row[subRowKey])
		.map((row) => ({
			...row,
			subRows: [] as (typeof row)[],
		}));

	for (const row of roots) {
		const subRows = rows.filter((r) => r[subRowKey] === row[itemKey]);
		if (subRows.length > 0) {
			row.subRows = subRows;
		} else {
			row.subRows = [];
		}
	}

	return roots;
}

export function createColumnMenuOptions(arg0: CreateColumnMenuOptionsBuilder) {
	return arg0;
}
